"use strict";
/* eslint-disable @typescript-eslint/naming-convention */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getCtxSchemaAtFlowNode = void 0;
const data_schema_core_1 = require("@tencent/data-schema-core");
const lodash_1 = require("lodash");
const mergeArray_1 = __importDefault(require("@tencent/data-schema-core/lib/utils/mergeArray"));
/**
 * 由于运行的先后顺序，运行到每一个节点时， ctx 的 schema 是不一样的。
 *
 * 此方法可以分析流程，并生成一个 getter 函数：
 *
 * - 传入 nodeId 即可得到对应时刻的 ctx schema
 * - 在 nodeId 前面加一个 `^` 可以返回进入此节点之前的 ctx schema
 * - 传入 null 或者无效值，得到最后一个节点（可能是）的 ctx schema，同时有 `isFallback: true`
 */
function getCtxSchemaAtFlowNode(opts) {
    var _a, _b;
    const getPatched = (0, lodash_1.memoize)(data_schema_core_1.getPatchedDataSchema);
    const { flow } = opts;
    const getSchemasFromNode = (node, nodeId) => {
        var _a, _b;
        const nodeType = node.type;
        const nodeMeta = opts.getNodeMeta(nodeType);
        const paramsSchema = getPatched((nodeMeta === null || nodeMeta === void 0 ? void 0 : nodeMeta.paramsSchema) || emptyObjectSchema);
        let outputSchemaRaw = (nodeMeta === null || nodeMeta === void 0 ? void 0 : nodeMeta.outputSchema) || emptyObjectSchema;
        if (typeof outputSchemaRaw === 'function') {
            try {
                outputSchemaRaw = outputSchemaRaw(node.params);
            }
            catch (_c) {
                outputSchemaRaw = emptyObjectSchema;
            }
        }
        const outputSchema = getPatched(outputSchemaRaw);
        const outputSchemaId = ((_a = outputSchema.fields) === null || _a === void 0 ? void 0 : _a.map(x => x === null || x === void 0 ? void 0 : x.id).filter(Boolean)) || [];
        const filteredParamFields = (_b = paramsSchema.fields) === null || _b === void 0 ? void 0 : _b.filter(x => x && !outputSchemaId.includes(x.id));
        const nodeDataSchema = {
            type: 'object',
            id: nodeId,
            title: node.title,
            description: `节点数据（包括节点的输入参数、输出数据）\n\n这是一个${(nodeMeta === null || nodeMeta === void 0 ? void 0 : nodeMeta.name) || nodeType}节点`,
            fields: (0, mergeArray_1.default)(outputSchema.fields, filteredParamFields, 'id'),
        };
        return {
            nodeDataSchema,
            /**
             * 这个节点的输出数据。相当于 nodeDataSchema 去掉了 paramsSchema
             */
            outputFields: outputSchema.fields,
        };
    };
    const initialCtxSchema = (0, data_schema_core_1.mergeDataSchema)({
        id: 'ctx',
        type: 'object',
        fields: [
            {
                id: 'state',
                title: '全局变量',
                // description: '你可以在这个下面挂载、传递数据。\n\n只能读取到前面执行过的步骤的输出。',
                type: 'object',
                fields: flow.stateSchema || [],
            },
            {
                id: 'params',
                type: 'object',
                title: '流程发起时的参数',
                fields: flow.params || [],
            },
            {
                id: 'nodes',
                type: 'object',
                title: '各节点数据',
                // description: '⚠ 建议不要直接用这个。请选择对应节点，并配置将想用的数据挂载到 state 上。',
                fields: [],
            },
        ],
    }, opts.initialCtxSchema || emptyObjectSchema);
    const scanQueue = [
        {
            nodeId: 'start',
            prev: {
                getCtxSchema: () => initialCtxSchema,
                node: null,
                prevNodeIds: [],
                nodeId: '<BOF>',
            },
        },
    ];
    const records = {};
    const tailIds = []; // 这些节点之后没有连接节点了
    const tailPlaceholderId = '<EOF>';
    while (scanQueue.length) {
        const { nodeId, prev } = scanQueue.shift();
        const visited = (_a = records[nodeId]) === null || _a === void 0 ? void 0 : _a.prevNodeIds.includes(prev.nodeId);
        if (visited)
            continue; // prev->此节点，这个链路发生过了，可能是图里有循环导致的。此次就不更新了
        const node = flow.nodes[nodeId];
        if (!node)
            continue; // next里面可能有脏数据
        const { nodeDataSchema } = getSchemasFromNode(node, nodeId);
        const getPrevSchema = prev.getCtxSchema;
        const getNewCtxSchema = () => {
            const prevSchema = getPrevSchema();
            return {
                ...prevSchema,
                fields: (0, lodash_1.map)(prevSchema.fields, (field) => {
                    if (field.id === 'nodes') {
                        // 更新此节点的 node data
                        return { ...field, fields: (0, mergeArray_1.default)(field.fields, [nodeDataSchema], 'id') };
                    }
                    // 无关字段，原样返回
                    return field;
                }),
            };
        };
        const curr = {
            node,
            nodeId,
            getCtxSchema: memo(getNewCtxSchema),
            prevNodeIds: (((_b = records[nodeId]) === null || _b === void 0 ? void 0 : _b.prevNodeIds) || []).concat(prev.nodeId),
        };
        records[nodeId] = curr;
        records[`^${nodeId}`] = prev;
        // 添加更多子项
        const newQueueItems = (0, lodash_1.values)(node.next)
            .filter(Boolean)
            .map(nodeId => ({ nodeId, prev: curr }));
        if (!newQueueItems.length)
            tailIds.push(nodeId);
        else
            scanQueue.push(...newQueueItems);
    }
    const tailRecord = {
        node: null,
        prevNodeIds: tailIds,
        nodeId: tailPlaceholderId,
        getCtxSchema: memo(() => {
            const nodesFields = [];
            const prevSchema = records[tailIds[0]].getCtxSchema();
            tailIds.forEach((nodeId) => {
                nodesFields.push(...records[nodeId].getCtxSchema().fields.find(x => x.id === 'nodes').fields);
            });
            return {
                ...prevSchema,
                fields: (0, lodash_1.map)(prevSchema.fields, (field) => {
                    if (field.id === 'nodes')
                        return { ...field, fields: (0, lodash_1.uniqBy)(nodesFields, 'id') };
                    return field;
                }),
            };
        }),
    };
    const getCtxSchemaAt = (nodeId) => {
        const record = records[nodeId] || tailRecord;
        return {
            node: flow.nodes[nodeId],
            nodeId,
            upstreamIds: record.prevNodeIds,
            ctxSchema: record.getCtxSchema(),
            isFallback: nodeId === tailPlaceholderId,
        };
    };
    getCtxSchemaAt.records = records;
    getCtxSchemaAt.getSchemasFromNode = (nodeId) => getSchemasFromNode(flow.nodes[nodeId], nodeId);
    return getCtxSchemaAt;
}
exports.getCtxSchemaAtFlowNode = getCtxSchemaAtFlowNode;
const emptyObjectSchema = (0, data_schema_core_1.getPatchedDataSchema)({ type: 'object', fields: [] });
function memo(fn) {
    let called = false;
    let lastValue;
    return (() => {
        if (!called) {
            lastValue = fn();
            called = true;
        }
        return lastValue;
    });
}
//# sourceMappingURL=getCtxSchemaAtFlowNode.js.map